import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Sparkles, Save } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ResultRow, animalNames, animalEmojis } from "./constants";
import { salvarPalpites } from "./palpitesUtils";

interface PalpitesIASectionProps {
  filtered: ResultRow[];
  source: 'rio' | 'federal';
  filtrosGeracao: Record<string, unknown>;
}

interface Palpites {
  milhares: string[];
  centenas: string[];
  dezenas: string[];
  grupos: number[];
  explicacao: string;
}

const PalpitesIASection: React.FC<PalpitesIASectionProps> = ({ filtered, source, filtrosGeracao }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [palpites, setPalpites] = useState<Palpites | null>(null);

  const buildStatsSummary = () => {
    const grupoCount: Record<number, number> = {};
    const dezenaCount: Record<string, number> = {};
    const milharCount: Record<string, number> = {};

    filtered.forEach(r => {
      grupoCount[r.grupo] = (grupoCount[r.grupo] || 0) + 1;
      dezenaCount[r.milhar.slice(-2)] = (dezenaCount[r.milhar.slice(-2)] || 0) + 1;
      milharCount[r.milhar] = (milharCount[r.milhar] || 0) + 1;
    });

    const topGrupos = Object.entries(grupoCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([g, c]) => `G${g}(${animalNames[parseInt(g)]}):${c}x`);

    const topDezenas = Object.entries(dezenaCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([d, c]) => `${d}:${c}x`);

    const topMilhares = Object.entries(milharCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([m, c]) => `${m}:${c}x`);

    return {
      total_registros: filtered.length,
      fonte: source === 'rio' ? 'Paratodos Rio' : 'Federal',
      top_grupos: topGrupos.join(', '),
      top_dezenas: topDezenas.join(', '),
      top_milhares: topMilhares.join(', '),
    };
  };

  const handleGenerate = async () => {
    if (filtered.length === 0) {
      toast({ title: "Sem dados", description: "Selecione um período com resultados", variant: "destructive" });
      return;
    }

    setOpen(true);
    setLoading(true);
    setPalpites(null);

    try {
      const stats = buildStatsSummary();
      const { data, error } = await supabase.functions.invoke('generate-palpites', {
        body: { source, stats },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setPalpites(data.palpites);
    } catch (err: any) {
      console.error('Erro ao gerar palpites:', err);
      toast({ title: "Erro", description: err.message || "Não foi possível gerar palpites", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!palpites) return;

    setSaving(true);
    try {
      await salvarPalpites({
        palpites,
        source,
        origem: "ia",
        filtros: {
          ...filtrosGeracao,
          total: filtered.length,
          total_resultados_analisados: filtered.length,
          total_sorteios_analisados: new Set(filtered.map((item) => `${item.data}|${item.turno}`)).size,
        },
      });
      toast({ title: "Salvo!", description: "Palpites salvos para conferência automática" });
    } catch (err: any) {
      toast({ title: "Erro ao salvar", description: err.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button onClick={handleGenerate} variant="outline" className="gap-2 border-primary/50 text-primary w-full sm:w-auto">
        <Sparkles className="h-4 w-4" />
        Palpite Guru IA
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Palpite Guru IA - {source === 'rio' ? 'Paratodos Rio' : 'Federal'}
            </DialogTitle>
            <DialogDescription>
              Palpites gerados por inteligência artificial baseados nas estatísticas
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">A IA está analisando os dados...</p>
            </div>
          ) : palpites ? (
            <div className="space-y-4">
              {palpites.milhares?.length > 0 && (
                <PalpiteSection title="🎰 Milhares" items={palpites.milhares} />
              )}
              {palpites.centenas?.length > 0 && (
                <PalpiteSection title="💯 Centenas" items={palpites.centenas} />
              )}
              {palpites.dezenas?.length > 0 && (
                <PalpiteSection title="🔢 Dezenas" items={palpites.dezenas} />
              )}
              {palpites.grupos?.length > 0 && (
                <Card>
                  <CardHeader className="py-2 px-4">
                    <CardTitle className="text-sm">🐾 Grupos</CardTitle>
                  </CardHeader>
                  <CardContent className="px-4 pb-3 pt-0">
                    <div className="flex flex-wrap gap-1.5">
                      {palpites.grupos.map((g, i) => (
                        <Badge key={i} variant="secondary" className="font-mono text-xs">
                          G{String(g).padStart(2, '0')} {animalEmojis[g] || ''} {animalNames[g] || ''}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              {palpites.explicacao && (
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-muted-foreground">{palpites.explicacao}</p>
                  </CardContent>
                </Card>
              )}
              <Button onClick={handleSave} disabled={saving} className="w-full gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? "Salvando..." : "Salvar para Conferência"}
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
};

const PalpiteSection: React.FC<{ title: string; items: string[] }> = ({ title, items }) => (
  <Card>
    <CardHeader className="py-2 px-4">
      <CardTitle className="text-sm">{title}</CardTitle>
    </CardHeader>
    <CardContent className="px-4 pb-3 pt-0">
      <div className="flex flex-wrap gap-1.5">
        {items.map((item, i) => (
          <Badge key={i} variant="secondary" className="font-mono text-xs">
            {item}
          </Badge>
        ))}
      </div>
    </CardContent>
  </Card>
);

export default PalpitesIASection;
